import React from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

export type DialogSize = 'compact' | 'default' | 'large';

const SIZE_MAP: Record<DialogSize, number> = {
  compact: 400,
  default: 480,
  large: 560,
};

interface DialogContentProps {
  children: React.ReactNode;
  scale: SharedValue<number>;
  contentOpacity: SharedValue<number>;
  size: DialogSize;
  /** Numeric override for the dialog's maxWidth. Takes precedence over `size`. */
  maxWidthOverride?: number;
}

export const DialogContent: React.FC<DialogContentProps> = ({
  children,
  scale,
  contentOpacity,
  size,
  maxWidthOverride,
}) => {
  const { colors } = useThemeColors();
  const animatedStyle = useAnimatedStyle(() => ({
    opacity: contentOpacity.value,
    transform: [{ scale: scale.value }],
  }));

  return (
    <View style={styles.centerer} pointerEvents="box-none">
      <Animated.View
        style={[
          styles.dialog,
          { maxWidth: maxWidthOverride ?? SIZE_MAP[size], backgroundColor: colors.bg.modal, borderColor: colors.card.border },
          animatedStyle,
        ]}
      >
        {children}
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  centerer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialog: {
    width: '90%',
    maxHeight: '85%',
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
  },
});
